/**
 * TeleTime POS - Main Application
 * Two-tier auth routing with appState machine
 *
 * appState flow:
 *   uninitialized → DeviceInitScreen
 *   device_ready   → PinLockScreen
 *   locked          → PinLockScreen
 *   user_active     → Main app routes
 */

import { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useRegister } from './context/RegisterContext';
import { useConnectionStatus } from './hooks/useConnectionStatus';
import { ConnectionBanner } from './components/ConnectionBanner';
import ErrorBoundary from './components/ErrorBoundary';

// Auth screens (loaded eagerly — they're the first thing users see)
import DeviceInitScreen from './pages/DeviceInitScreen';
import PinLockScreen from './pages/PinLockScreen';
import NotFound from './pages/NotFound';

// Legacy login (kept for backward compat)
import Login from './pages/Login';

// Lazy-loaded pages for code-splitting
const POSMain = lazy(() => import('./pages/POSMain'));
const Reports = lazy(() => import('./pages/Reports'));
const OverrideAuditReport = lazy(() => import('./pages/OverrideAuditReport'));
const AdminFinancingPage = lazy(() => import('./pages/AdminFinancingPage'));
const CustomerFinancingPage = lazy(() => import('./pages/CustomerFinancingPage'));
const MyCommissionsPage = lazy(() => import('./pages/MyCommissionsPage'));
const TeamCommissionsPage = lazy(() => import('./pages/TeamCommissionsPage'));
const CommissionRulesPage = lazy(() => import('./pages/CommissionRulesPage'));
const PayrollSummaryPage = lazy(() => import('./pages/PayrollSummaryPage'));
const ReturnInitiation = lazy(() => import('./pages/ReturnInitiation'));
const TransactionsPage = lazy(() => import('./pages/Transactions'));

// Lazy-loaded report components
const ShiftReportPage = lazy(() =>
  import('./components/Reports').then(mod => ({ default: mod.ShiftReportPage }))
);

// Lazy-loaded admin components
const ApprovalRulesPage = lazy(() =>
  import('./components/Admin').then(mod => ({ default: mod.ApprovalRulesPage }))
);
const ApprovalAnalyticsPage = lazy(() =>
  import('./components/approvals/ApprovalAnalytics').then(mod => ({ default: mod.ApprovalAnalytics }))
);
const TierSettingsAdminPage = lazy(() =>
  import('./components/approvals/TierSettingsAdmin').then(mod => ({ default: mod.TierSettingsAdmin }))
);

// Register Management
import {
  RegisterSelect,
  OpenRegister,
  CloseRegister,
  PrintableShiftReport,
} from './components/Register';

// Notifications
import { ExpiringQuotesToast } from './components/Notifications';
import BugReportForm from './components/BugReportForm';

// ============================================================================
// ENVIRONMENT BADGE
// ============================================================================

function DevBadge() {
  const isDev = import.meta.env.DEV;
  const mode = import.meta.env.MODE;

  if (!isDev) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[9999] pointer-events-none">
      <div className="bg-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold uppercase shadow-lg">
        {mode === 'development' ? 'DEV' : mode.toUpperCase()}
      </div>
    </div>
  );
}

// ============================================================================
// LOADING SPINNER
// ============================================================================

function LoadingScreen({ message = 'Loading...' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-800">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">{message}</p>
      </div>
    </div>
  );
}

// ============================================================================
// ROUTE GUARDS
// ============================================================================

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, appState } = useAuth();

  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // Two-tier: if not user_active, the App component handles routing
  if (!isAuthenticated || appState !== 'user_active') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function ManagerRoute({ children }) {
  const { isAuthenticated, loading, isAdminOrManager, appState } = useAuth();

  if (loading) {
    return <LoadingScreen message="Checking permissions..." />;
  }

  if (!isAuthenticated || appState !== 'user_active') {
    return <Navigate to="/" replace />;
  }

  if (!isAdminOrManager()) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function ShiftRequired({ children }) {
  const { hasActiveShift, loading, isInitialized } = useRegister();

  if (loading || !isInitialized) {
    return <LoadingScreen message="Loading register..." />;
  }

  if (!hasActiveShift) {
    return <Navigate to="/open-shift" replace />;
  }

  return children;
}

// ============================================================================
// OPEN / CLOSE SHIFT FLOWS
// ============================================================================

function OpenShiftPage() {
  const navigate = useNavigate();
  const { hasActiveShift, selectedRegister, selectRegister } = useRegister();
  const [step, setStep] = useState('select');

  if (hasActiveShift) {
    return <Navigate to="/" replace />;
  }

  const handleSelectRegister = (register) => {
    selectRegister(register);
    setStep('open');
  };

  const handleBack = () => setStep('select');
  const handleShiftOpened = () => navigate('/', { replace: true });

  if (step === 'select' || !selectedRegister) {
    return <RegisterSelect onSelectRegister={handleSelectRegister} />;
  }

  return (
    <OpenRegister
      register={selectedRegister}
      onBack={handleBack}
      onComplete={handleShiftOpened}
    />
  );
}

function CloseShiftPage() {
  const navigate = useNavigate();
  const { hasActiveShift, currentShift, shiftSummary } = useRegister();
  const [showReport, setShowReport] = useState(false);
  const [closingData, setClosingData] = useState(null);

  if (!hasActiveShift) {
    return <Navigate to="/open-shift" replace />;
  }

  const handleBack = () => navigate('/', { replace: true });
  const handleComplete = (data) => setClosingData(data);
  const handlePrintReport = (data) => {
    setClosingData(data);
    setShowReport(true);
  };

  // The variance the close stored (or, printed before closing, the one CloseRegister showed
  // against the server's expected cash) — never re-derived here.
  const variance = Number.isFinite(closingData?.variance) ? closingData.variance : 0;

  return (
    <>
      <CloseRegister
        onBack={handleBack}
        onComplete={handleComplete}
        onPrintReport={handlePrintReport}
      />
      {showReport && (
        <PrintableShiftReport
          shift={currentShift}
          summary={shiftSummary?.summary}
          closingCash={closingData?.closingCash}
          variance={variance}
          onClose={() => {
            setShowReport(false);
            navigate('/open-shift', { replace: true });
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// MAIN APP — appState-based routing
// ============================================================================

function App() {
  const { isAuthenticated, user, appState, loading, deviceInfo } = useAuth();
  const { status: connectionStatus } = useConnectionStatus();
  const location = useLocation();

  // Loading state
  if (loading) {
    return <LoadingScreen message="Starting up..." />;
  }

  // ── Two-Tier Auth Gates ────────────────────────────────────────────
  // uninitialized: show device init screen
  if (appState === 'uninitialized') {
    return (
      <>
        <DevBadge />
        <DeviceInitScreen />
      </>
    );
  }

  // device_ready or locked: show PIN screen
  if (appState === 'device_ready' || appState === 'locked') {
    return (
      <>
        <DevBadge />
        <PinLockScreen />
      </>
    );
  }

  // ── user_active: full app ──────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100">
      {isAuthenticated && <ConnectionBanner status={connectionStatus} />}

      <DevBadge />

      {isAuthenticated && (
        <ExpiringQuotesToast
          salesRepId={user?.id}
          showOnLoad={true}
          onViewQuotes={(filter) => {
            window.location.href = `/quotes?filter=expiring&view=${filter}`;
          }}
        />
      )}

      <Suspense fallback={<LoadingScreen message="Loading page..." />}>
        {/* Route-scoped error boundary: a render error in a single page shows
            an inline fallback while keeping the connection banner, dev badge,
            and ExpiringQuotesToast functional. key={pathname} auto-resets the
            boundary when the user navigates. */}
        <ErrorBoundary scope="route" key={location.pathname}>
        <Routes>
          {/* ============================================ */}
          {/* LEGACY LOGIN (redirect to PIN flow) */}
          {/* ============================================ */}
          <Route path="/login" element={<Navigate to="/" replace />} />

          {/* ============================================ */}
          {/* PROTECTED ROUTES - Require Authentication */}
          {/* ============================================ */}

          <Route
            path="/open-shift"
            element={
              <ProtectedRoute>
                <OpenShiftPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/close-shift"
            element={
              <ProtectedRoute>
                <CloseShiftPage />
              </ProtectedRoute>
            }
          />

          <Route path="/close-register" element={<Navigate to="/close-shift" replace />} />
          <Route path="/shift-close" element={<Navigate to="/close-shift" replace />} />

          {/* ============================================ */}
          {/* MANAGER ROUTES */}
          {/* ============================================ */}

          <Route
            path="/reports"
            element={<ManagerRoute><Reports /></ManagerRoute>}
          />
          <Route
            path="/reports/overrides"
            element={<ManagerRoute><OverrideAuditReport /></ManagerRoute>}
          />
          <Route
            path="/reports/shift"
            element={<ManagerRoute><ShiftReportPage /></ManagerRoute>}
          />
          <Route
            path="/transactions"
            element={<ManagerRoute><TransactionsPage /></ManagerRoute>}
          />

          {/* ============================================ */}
          {/* ADMIN SETTINGS */}
          {/* ============================================ */}

          <Route
            path="/admin/approval-rules"
            element={<ManagerRoute><ApprovalRulesPage /></ManagerRoute>}
          />
          <Route
            path="/admin/financing"
            element={<ManagerRoute><AdminFinancingPage /></ManagerRoute>}
          />
          <Route
            path="/admin/approval-analytics"
            element={<ManagerRoute><ApprovalAnalyticsPage /></ManagerRoute>}
          />
          <Route
            path="/admin/tier-settings"
            element={<ManagerRoute><TierSettingsAdminPage /></ManagerRoute>}
          />

          {/* ============================================ */}
          {/* COMMISSION PAGES */}
          {/* ============================================ */}

          <Route
            path="/commissions/my"
            element={<ProtectedRoute><MyCommissionsPage /></ProtectedRoute>}
          />
          <Route
            path="/commissions/team"
            element={<ManagerRoute><TeamCommissionsPage /></ManagerRoute>}
          />
          <Route
            path="/admin/commissions/rules"
            element={<ManagerRoute><CommissionRulesPage /></ManagerRoute>}
          />
          <Route
            path="/admin/commissions/payroll"
            element={<ManagerRoute><PayrollSummaryPage /></ManagerRoute>}
          />

          {/* ============================================ */}
          {/* CUSTOMER PAGES */}
          {/* ============================================ */}

          <Route
            path="/customer/:customerId/financing"
            element={<ProtectedRoute><CustomerFinancingPage /></ProtectedRoute>}
          />

          {/* ============================================ */}
          {/* RETURNS */}
          {/* ============================================ */}

          <Route
            path="/returns"
            element={
              <ProtectedRoute>
                <ShiftRequired><ReturnInitiation /></ShiftRequired>
              </ProtectedRoute>
            }
          />

          {/* ============================================ */}
          {/* MAIN POS - Requires Active Shift */}
          {/* ============================================ */}

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ShiftRequired><POSMain /></ShiftRequired>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pos"
            element={
              <ProtectedRoute>
                <ShiftRequired><POSMain /></ShiftRequired>
              </ProtectedRoute>
            }
          />

          {/* ============================================ */}
          {/* 404 */}
          {/* ============================================ */}

          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
      </Suspense>

      {isAuthenticated && (
        <BugReportForm reportedBy={user?.name || user?.email || 'Staff'} />
      )}
    </div>
  );
}

export default App;
